// @ts-strict-ignore
import { Component, OnDestroy } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { InfiniteScrollCustomEvent, ViewWillEnter } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, take } from "rxjs/operators";
import { Pagination } from "src/app/shared/service/pagination";
import { Edge, Service, Utils, Websocket } from "src/app/shared/shared";
import { Role } from "src/app/shared/type/role";
import { environment } from "src/environments";
import { ChosenFilter } from "../filter/filter.component";

@Component({
    selector: "overview",
    templateUrl: "./overview.component.html",
    styleUrls: ["./overview.component.css"],
})
export class OverViewComponent implements ViewWillEnter, OnDestroy {
    public environment = environment;
    /** True, if there is no access to any Edge. */
    public noEdges: boolean = false;

    /**  True, if the logged in user is allowed to install new edges. */
    public loggedInUserCanInstall: boolean = false;

    public form: FormGroup;
    public filteredEdges: Edge[] = [];

    public isAnonymized: boolean = false; // oEMS

    protected searchParams: Map<string, ChosenFilter["value"]> = new Map();

    // Stores the ID of the latest loadEdges() request. This is used to prevent a race condition running multiple search request quickly.
    protected latestLoadEdgesRequestId: string | null = null;

    private stopOnDestroy: Subject<void> = new Subject<void>();
    private page = 0;
    private query: string | null = null;

    /** Limits edges in pagination response */
    private readonly limit: number = 20;
    /** True, if all available edges for this user had been retrieved */
    private limitReached: boolean = false;

    constructor(
        public service: Service,
        public websocket: Websocket,
        public utils: Utils,
        private router: Router,
        private route: ActivatedRoute,
        public translate: TranslateService,
        public pagination: Pagination,
    ) { }

    ionViewWillEnter() {
        this.clearSearchResults();
        this.service.metadata.pipe(filter(metadata => !!metadata), take(1)).subscribe((metadata) => { // oEMS
            // oEMS START
            if (metadata.user.name.startsWith("_")) {
                this.isAnonymized = true;
            }
            // oEMS END
            this.init();
        });
    }

    /**
     * Updates available edges on scroll-event
     *
     * @param infiniteScroll the InfiniteScrollCustomEvent
     */
    doInfinite(infiniteScroll: InfiniteScrollCustomEvent) {
        setTimeout(() => {
            this.loadNextPage().then((edges) => {
                this.addToFilteredEdges(edges);
                infiniteScroll.target.complete();
            }).catch(() => {
                infiniteScroll.target.complete();
            });
        }, 200);
    }

    ngOnDestroy() {
        this.stopOnDestroy.next();
        this.stopOnDestroy.complete();
    }

    loadNextPage(): Promise<Edge[]> {
        const requestId = crypto.randomUUID();
        this.latestLoadEdgesRequestId = requestId;
        if (this.environment.debugMode) {
            console.log("New loadNextPage request started with ID " + requestId);
        }

        return new Promise<Edge[]>((resolve, reject) => {
            if (this.limitReached) {
                resolve([]);
                return;
            }

            const searchParamsObj = {};
            if (this.searchParams && this.searchParams.size > 0) {
                for (const [key, value] of this.searchParams) {
                    searchParamsObj[key] = value;
                }
            }
            this.service.getEdges(this.page, this.query, this.limit, searchParamsObj)
                .then((edges) => {
                    // Drop response of outdated requests to prevent showing outdated results.
                    if (requestId !== this.latestLoadEdgesRequestId) {
                        if (this.environment.debugMode) {
                            console.log("LoadNextPage request with ID " + requestId + " was ignored because theres a newer request pending with ID " + this.latestLoadEdgesRequestId);
                        }
                        reject(new Error("Request was ignored because it's outdated."));
                        return;
                    }

                    this.limitReached = edges.length < this.limit;
                    this.page++;
                    this.latestLoadEdgesRequestId = null;
                    resolve(edges);
                }).catch((err) => {
                    reject(err);
                });
        });
    }

    protected getAndSubscribeEdge(edge: Edge) {
        this.pagination.getAndSubscribeEdge(edge);
    }

    protected isLoading(): boolean {
        return this.latestLoadEdgesRequestId != null;
    }

    /**
     * Search on change, triggered by searchbar input-event.
     *
     * @param event from template passed event
     */
    protected searchOnChange(searchParams?: Map<string, ChosenFilter["value"]>) {

        if (searchParams) {
            this.searchParams = searchParams;
        }

        // Clear search results to only show the loading circle and no results.
        this.clearSearchResults();

        this.loadNextPage().then((edges) => {
            this.addToFilteredEdges(edges);

            // Catch and ignore errors because loadNextPage() may fail due to outdated request.
        }).catch((err) => { });
    }

    private init() {
        this.loadNextPage().then((edges) => {
            this.service.metadata
                .pipe(
                    filter(metadata => !!metadata),
                    take(1),
                )
                .subscribe(metadata => {
                    const edgeIds = Object.keys(metadata.edges);
                    this.noEdges = edgeIds.length === 0;
                    this.loggedInUserCanInstall = Role.isAtLeast(metadata.user.globalRole, "installer");

                    // Forward directly to device page, if
                    // - Direct local access to Edge
                    // - No installer (i.e. guest or owner) and access to only one Edge
                    if (environment.backend == "OpenEMS Edge" || (!this.loggedInUserCanInstall && edgeIds.length == 1)) {
                        const edge = metadata.edges[edgeIds[0]];
                        setTimeout(() => {
                            this.router.navigate(["/device", edge.id]);
                        }, 100);
                        return;
                    }
                    this.filteredEdges = [];
                    this.addToFilteredEdges(edges);
                });

            // Catch and ignore errors because loadNextPage() may fail due to outdated request.
        }).catch((err) => { });
    }

    /**
     * Adds the given list of edges to the filtered edges. Ensures that only unique edges are added.
     * @param newEdges list of edges to add
     */
    private addToFilteredEdges(newEdges: Edge[]) {
        this.filteredEdges.push(...
            newEdges.filter(newEdge =>
                !this.filteredEdges.some(existingEdge => existingEdge.id === newEdge.id),
            ));
    }

    /**
     * Clears the search results and resets the pagination.
     */
    private clearSearchResults() {
        this.filteredEdges = [];
        this.page = 0;
        this.limitReached = false;
    }
}
